import { z, ZodTypeAny } from "zod";
import { allFields, formConfig, FieldConfig, onFormConfigChange } from "./formConfig";

function fieldSchema(f: FieldConfig): ZodTypeAny {
  switch (f.type) {
    case "text_no_input":
    case "file_no_input":
      return z.undefined();
    case "text":
    case "textarea":
    case "address": {
      let s = z.string();
      if (f.required) s = s.min(1, "Required");
      return s;
    }
    case "currency": {
      let s = z.string();
      if (f.required) s = s.min(1, "Required");
      return s;
    }
    case "email": {
      const s = z.string().email("Invalid email");
      if (!f.required) return s.or(z.literal(""));
      return s;
    }
    case "date": {
      let s = z.string();
      if (f.required) s = s.min(1, "Required");
      return s;
    }
    case "radio":
    case "radio_vertical": {
      const opts = f.options ?? [];
      let s: ZodTypeAny = z.enum(opts as [string, ...string[]]);
      if (!f.required) s = s.or(z.literal(""));
      return s;
    }
    case "checkbox":
    case "checkbox_vertical": {
      let s = z.array(z.string());
      if (f.required) s = s.min(1, "Select at least one");
      return s;
    }
    case "likert": {
      const rows = f.likertRows ?? [];
      const cols = f.likertColumns ?? [];
      const shape: Record<string, ZodTypeAny> = {};
      for (const r of rows) {
        let s: ZodTypeAny = z.enum(cols as [string, ...string[]]);
        if (!f.required) s = s.or(z.literal(""));
        shape[r] = s;
      }
      return z.object(shape);
    }
    case "file": {
      const uploadedFile = z.object({ name: z.string(), url: z.string().url() });
      const files = z.array(uploadedFile);
      if (f.required) return z.union([uploadedFile, files.min(1, "Required")]);
      return z.union([uploadedFile, files, z.undefined()]);
    }
    case "person": {
      const obj = z
        .object({
          displayName: z.string().min(1),
          mail: z.string().optional(),
          userPrincipalName: z.string().optional(),
          jobTitle: z.string().optional(),
          department: z.string().optional(),
          id: z.string().optional(),
          isFreeform: z.boolean().optional()
        })
        .passthrough();
      let arr = z.array(obj);
      if (f.required) arr = arr.min(1, "Required");
      if (typeof f.maxPersons === "number" && f.maxPersons > 0) {
        arr = arr.max(f.maxPersons, `Select at most ${f.maxPersons}`);
      }
      // Accept the multi-select array (primary) plus legacy single object / string values.
      if (f.required) {
        return z.union([arr, obj, z.string().min(1)]);
      }
      return z.union([arr, obj, z.string(), z.literal(""), z.undefined()]);
    }
  }
}

function buildFormSchema() {
  const entries: [string, ZodTypeAny][] = [];
  for (const sec of formConfig) {
    for (const f of sec.fields) {
      entries.push([f.id, fieldSchema(sec.hidden ? { ...f, required: false } : f)]);
    }
  }
  return z.object(Object.fromEntries(entries));
}
function buildSectionSchemas() {
  return formConfig.map((sec) =>
    z.object(
      Object.fromEntries(
        sec.fields.map((f) => [f.id, fieldSchema(sec.hidden ? { ...f, required: false } : f)])
      )
    )
  );
}

export let formSchema = buildFormSchema();
export let sectionSchemas = buildSectionSchemas();

onFormConfigChange(() => {
  formSchema = buildFormSchema();
  sectionSchemas = buildSectionSchemas();
});

export type FormValues = Record<string, unknown>;

export function buildDefaultValues(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of allFields) {
    if (f.type === "text_no_input" || f.type === "file_no_input") continue;
    if (f.type === "checkbox" || f.type === "checkbox_vertical") out[f.id] = [];
    else if (f.type === "likert") {
      out[f.id] = Object.fromEntries((f.likertRows ?? []).map((r) => [r, ""]));
    } else if (f.type === "file") out[f.id] = undefined;
    else if (f.type === "person") out[f.id] = [];
    else if (f.type === "currency") out[f.id] = "";
    else out[f.id] = "";
  }
  return out;
}

export type SectionStatus = "ok" | "pending" | "error";

export interface SectionResult {
  status: SectionStatus;
  missing: { sectionTitle: string; question: string }[];
}

function hasAnyContent(values: Record<string, unknown>, sec: typeof formConfig[number]): boolean {
  for (const f of sec.fields) {
    const v = values[f.id];
    if (v == null) continue;
    if (typeof v === "string" && v.trim() !== "") return true;
    if (Array.isArray(v) && v.length > 0) return true;
    if (typeof v === "object") {
      const o = v as Record<string, unknown>;
      if (f.type === "file" && typeof o.url === "string") return true;
      if (f.type === "person" && typeof o.displayName === "string" && o.displayName.trim() !== "") return true;
      if (f.type === "likert") {
        for (const k of Object.keys(o)) {
          if (typeof o[k] === "string" && (o[k] as string).trim() !== "") return true;
        }
      }
    }
  }
  return false;
}

export function computeSectionStatus(
  values: Record<string, unknown>,
  touched: boolean,
  visited?: boolean[]
): SectionResult[] {
  return formConfig.map((sec, i) => {
    const slice: Record<string, unknown> = {};
    for (const f of sec.fields) slice[f.id] = (values as Record<string, unknown>)[f.id];
    const result = sectionSchemas[i].safeParse(slice);
    const missing: { sectionTitle: string; question: string }[] = [];
    if (!result.success) {
      const issuePaths = new Set(result.error.issues.map((iss) => String(iss.path[0])));
      for (const f of sec.fields) {
        if (issuePaths.has(f.id)) {
          missing.push({ sectionTitle: sec.title, question: f.question });
        }
      }
    }
    const hasRequired = sec.fields.some((f) => f.required);
    const wasVisited = visited ? !!visited[i] : true;
    let status: SectionStatus;
    if (result.success) {
      if (!hasRequired && !hasAnyContent(values, sec)) {
        status = "pending";
      } else {
        status = wasVisited || touched ? "ok" : "pending";
      }
    } else if (touched) {
      status = "error";
    } else {
      status = "pending";
    }
    return { status, missing };
  });
}
